import {useRef,useState} from 'react';
import {Lock,Paperclip,Repeat,ShieldAlert,Sparkles} from 'lucide-react';
import type {JourneyAction,JourneyStep,PolicyView} from '../../../../src/domain/policy/describe';
import {ApprovalLock,Fill,actionIcon,pressure} from './parts';

const minus=(n:number)=>n<0?`−${Math.abs(n)}`:String(n);

/** Big numeral plus a caption, so "7 days overdue" reads at a glance and to a screen reader. */
export function DayHeading({step,id}:{step:JourneyStep;id?:string}){
 const d=step.day;
 const [num,cap]=d===null?[step.dayLabel,step.when]:d===0?['Due','date']:d<0?[String(-d),`day${d===-1?'':'s'} before due`]
  :[String(d),`day${d===1?'':'s'} overdue`];
 return <h3 className="pj-day" id={id}><span className="pj-num">{num}</span><span className="pj-cap">{cap}</span></h3>;
}

function Action({action}:{action:JourneyAction}){
 if(action.kind==='email')return <div className="pj-act pj-email">
  <p className="pj-act-title">{actionIcon('email')}<strong>{action.title}</strong><ApprovalLock approval={action.approval}/></p>
  <p className="pj-route">From {action.sender} to {action.recipients}{action.personal?', written personally':''}</p>
  {action.writer==='model'&&action.intent&&<p className="pj-intent"><Sparkles size={11} aria-hidden/>
   <span><strong>Written by Alder in your voice.</strong> {action.intent}</span></p>}
  {action.openings&&(action.openings.default.length>0||action.openings.friday.length>0)&&<p className="pj-openings">
   Opens with {[action.openings.default[0]?`“${action.openings.default[0]}”`:null,
    action.openings.friday[0]?`“${action.openings.friday[0]}” on Fridays`:null].filter(Boolean).join('; ')}</p>}
  {action.subject&&<div className="pj-mail">{action.writer==='model'&&<p className="pj-template-label">Template</p>}
   <p className="pj-subject"><span className="sr-only">Subject: </span><Fill text={action.subject}/></p>
   {action.preview&&<p className="pj-preview"><Fill text={action.preview}/></p>}</div>}
  {action.attachments&&action.attachments.length>0&&<p className="pj-attach"><Paperclip size={11} aria-hidden/>
   {action.attachments.join(', ')}</p>}
 </div>;
 return <div className={`pj-act pj-${action.kind}`}>
  <p className="pj-act-title">{actionIcon(action.kind)}<span><Fill text={action.title}/>
   {action.owner&&<small> for the {action.owner}</small>}
  </span><ApprovalLock approval={action.approval}/></p>
 </div>;
}

/** Emails always show; when a step also opens several tasks, they fold behind one line so the rail stays short. */
function Actions({actions}:{actions:JourneyAction[]}){
 const emails=actions.filter(a=>a.kind==='email');const rest=actions.filter(a=>a.kind!=='email');
 return <div className="pj-actions">{emails.map((a,i)=><Action key={i} action={a}/>)}
  {rest.length>2?<details className="pj-more"><summary>{actionIcon('task')}<span>{rest.length} tasks for the team</span></summary>
   {rest.map((a,i)=><Action key={i} action={a}/>)}</details>:rest.map((a,i)=><Action key={`r${i}`} action={a}/>)}</div>;
}

function StepCard({step,tier,tierLabel,tiers,variations,maxSeverity,cardRef}:{step:JourneyStep;tier:string;tierLabel:string;
 tiers:PolicyView['journey']['tiers'];variations:string[];maxSeverity:number;cardRef:(el:HTMLElement|null)=>void}){
 const applies=tier==='all'||step.tiers.includes(tier);
 const actions=(tier==='all'?step.base:step.byTier[tier])?.actions??[];
 const headingId=`pj-step-${step.stageId}`;
 const named=step.day!==null&&step.label!==step.dayLabel&&!/^Day -?\d+$/.test(step.label);
 return <article ref={cardRef} tabIndex={-1} className="pj-card" data-applies={applies} style={pressure(step.severity,maxSeverity)}
  aria-labelledby={headingId}>
  <DayHeading step={step} id={headingId}/>
  {named&&<p className="pj-stage-name">{step.label}</p>}
  {step.day===null&&<p className="pj-stage-name">{step.label}</p>}
  {!applies?<p className="pj-skip">Not sent to {tierLabel} invoices.</p>:<>
   <Actions actions={actions}/>
   {step.condition&&<p className="pj-note">Only when {step.condition}</p>}
   {(step.repeatDays||step.extraReview)&&<p className="pj-flags">
    {step.repeatDays&&<span><Repeat size={11} aria-hidden/> Repeats every {step.repeatDays} days</span>}
    {step.extraReview&&<span><ShieldAlert size={11} aria-hidden/> Extra review before sending</span>}</p>}
   {tier==='all'&&<div className="pj-tiers">
    <ul aria-label="Tiers that get this step">{step.tiers.length===tiers.length?<li data-on="true">Every tier</li>
     :tiers.map(t=><li key={t.id} data-on={step.tiers.includes(t.id)}>
      <span className="sr-only">{step.tiers.includes(t.id)?'Sent to ':'Not sent to '}</span>{t.label}</li>)}</ul>
    {variations.length>0&&<ul className="pj-variations">{variations.map(v=><li key={v}><Fill text={v}/></li>)}</ul>}
   </div>}
  </>}
 </article>;
}

function Ruler({steps,tier,maxSeverity,onPick}:{steps:JourneyStep[];tier:string;maxSeverity:number;onPick:(id:string)=>void}){
 const days=steps.map(s=>s.day!);
 const min=Math.min(-8,...days.map(d=>d-1));const max=Math.max(91,...days.map(d=>d+1));
 // Before the due date gets a fixed share of the width so Day -1 and the due date never collide; after it, days are to scale.
 const split=0.2;
 const at=(d:number)=>`${(d<0?(d-min)/-min*split:split+d/max*(1-split))*100}%`;
 return <div className="pj-ruler" role="group" aria-label="Jump to a step on the timeline">
  <div className="pj-band pj-band-before" style={{left:0,width:at(0)}}><span>Before due</span></div>
  <div className="pj-band pj-band-after" style={{left:at(0),right:0}}><span>Overdue</span></div>
  <div className="pj-track" aria-hidden/>
  <div className="pj-due" style={{left:at(0)}} aria-hidden><span>Due date</span></div>
  {steps.map(s=>{const on=tier==='all'||s.tiers.includes(tier);
   return <button key={s.stageId} type="button" className="pj-dot" data-on={on}
    style={{left:at(s.day!),...pressure(s.severity,maxSeverity)}}
    onClick={()=>onPick(s.stageId)} aria-label={`${s.dayLabel}: ${s.label}${on?'':' (not sent to this tier)'}`}>
    {s.day!==0&&<span className="pj-dot-label" aria-hidden>{minus(s.day!)}</span>}</button>;})}
 </div>;
}

/** The dunning journey: a day ruler over a rail of step cards, filtered by tier. */
export function Journey({view}:{view:PolicyView}){
 const [tier,setTier]=useState('all');
 const cards=useRef(new Map<string,HTMLElement>());
 const {steps,tiers}=view.journey;
 const dated=steps.filter(s=>s.day!==null);const other=steps.filter(s=>s.day===null);
 const maxSeverity=Math.max(1,...steps.map(s=>s.severity));
 const variations=new Map(view.stages.map(s=>[s.id,s.variations]));
 const tierLabel=tiers.find(t=>t.id===tier)?.label??'';
 const pick=(id:string)=>{const el=cards.current.get(id);if(!el)return;
  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({behavior:reduce?'auto':'smooth',block:'nearest',inline:'center'});el.focus({preventScroll:true});};
 const card=(s:JourneyStep)=><li key={s.stageId}><StepCard step={s} tier={tier} tierLabel={tierLabel} tiers={tiers}
  variations={variations.get(s.stageId)??[]} maxSeverity={maxSeverity}
  cardRef={el=>{if(el)cards.current.set(s.stageId,el);else cards.current.delete(s.stageId);}}/></li>;
 const selected=tiers.find(t=>t.id===tier);
 return <section className="pj-panel pj-journey" aria-labelledby="pj-journey-title">
  <div className="pj-journey-head">
   <div><h2 id="pj-journey-title">The reminder journey</h2>
    <p>What happens to an unpaid invoice, counted from its due date.</p></div>
   {tiers.length>1&&<div className="pj-tier-toggle" role="group" aria-label="Show the journey for a tier">
    {[{id:'all',label:'All tiers'},...tiers].map(t=><button key={t.id} type="button" aria-pressed={tier===t.id}
     onClick={()=>setTier(t.id)}>{t.label}</button>)}</div>}
  </div>
  <p className="pj-legend"><Lock size={11} aria-hidden/> Needs approval: a person approves it before it goes out</p>
  <p className="pj-tier-rule" aria-live="polite">{selected?<><strong>{selected.label}</strong>: {selected.rule}</>
   :`${tiers.length} tier${tiers.length===1?'':'s'}. Pick one to see exactly what its customers receive.`}</p>
  {dated.length>0&&<Ruler steps={dated} tier={tier} maxSeverity={maxSeverity} onPick={pick}/>}
  <ol className="pj-rail" aria-label="Steps by day" tabIndex={0}>{dated.map(card)}</ol>
  {other.length>0&&<><h3 className="pj-subhead">Timed from other events</h3>
   <ol className="pj-rail pj-rail-other" aria-label="Steps timed from other events">{other.map(card)}</ol></>}
 </section>;
}
